import React, { useCallback, useEffect, useRef, useState } from 'react'
import { format } from 'date-fns'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { useLeadDetail, useLeadMeetingActivities } from '../../hooks/useLeadDetail'
import {
  PIPELINE_LEAD_STATUSES,
  PIPELINE_STATUS_LABELS,
  updateLeadNotes,
  updateLeadStatus
} from '../../services/leadsService'

const { FiX, FiChevronDown, FiCalendar } = FiIcons

const STATUS_BADGE_COLORS = {
  new: 'bg-gray-100 text-gray-800 ring-gray-200',
  contacted: 'bg-blue-100 text-blue-800 ring-blue-200',
  '1_1_completed': 'bg-purple-100 text-purple-800 ring-purple-200',
  converted: 'bg-green-100 text-green-800 ring-green-200',
  lost: 'bg-red-100 text-red-800 ring-red-200'
}

const formatStatusLabel = (status) =>
  PIPELINE_STATUS_LABELS[status] ??
  (status ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ') : '—')

const resolveAgentOwner = (lead) => {
  const owner = lead?.users
  if (Array.isArray(owner)) return owner[0] ?? null
  return owner ?? null
}

const resolveSharingLinkLabel = (lead) => {
  const slug = lead?.sharing_link_slug?.trim()
  if (slug) return slug
  const asl = lead?.agent_sharing_links
  const link = Array.isArray(asl) ? asl[0] : asl
  if (link?.slug) return link.slug
  return '—'
}

const sortSurveyResponses = (responses) =>
  [...(responses ?? [])].sort((a, b) => {
    const ta = a?.submitted_at ? new Date(a.submitted_at).getTime() : 0
    const tb = b?.submitted_at ? new Date(b.submitted_at).getTime() : 0
    return tb - ta
  })

const useEscapeClose = (open, onClose) => {
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
}

const StatusBadgeDropdown = ({ status, onChange, disabled }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const colorClass = STATUS_BADGE_COLORS[status] ?? STATUS_BADGE_COLORS.new

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ring-1 transition-opacity hover:opacity-90 disabled:opacity-50 ${colorClass}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {formatStatusLabel(status)}
        <SafeIcon icon={FiChevronDown} className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-1 z-20 min-w-[10rem] py-1 bg-white border border-gray-200 rounded-xl shadow-lg"
        >
          {PIPELINE_LEAD_STATUSES.map((value) => (
            <li key={value}>
              <button
                type="button"
                role="option"
                aria-selected={value === status}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${
                  value === status ? 'font-medium text-slate-900' : 'text-slate-700'
                }`}
                onClick={() => {
                  setOpen(false)
                  if (value !== status) onChange(value)
                }}
              >
                <span
                  className={`w-2 h-2 rounded-full shrink-0 ${
                    (STATUS_BADGE_COLORS[value] ?? '').split(' ')[0] ?? 'bg-gray-200'
                  }`}
                />
                {formatStatusLabel(value)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const DetailField = ({ label, children }) => (
  <div>
    <p className="text-sm text-gray-600">{label}</p>
    <p className="font-medium text-gray-900 mt-0.5 break-words">{children}</p>
  </div>
)

/**
 * Shared lead detail modal for pipeline and survey responses.
 * @param {{
 *   leadId?: string | null,
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onStatusChange?: () => void,
 *   defaultTab?: 'details' | 'surveys'
 * }} props
 */
const LeadDetailModal = ({ leadId, isOpen, onClose, onStatusChange, defaultTab = 'details' }) => {
  const { role, profile, user } = useAuth()
  const auth = {
    role: profile?.role ?? role,
    userId: profile?.id ?? user?.id
  }

  const [activeTab, setActiveTab] = useState(defaultTab)
  const [status, setStatus] = useState('new')
  const [notes, setNotes] = useState('')
  const [savingNotes, setSavingNotes] = useState(false)
  const [savingStatus, setSavingStatus] = useState(false)

  useEscapeClose(isOpen, onClose)

  const queryEnabled = isOpen && Boolean(leadId)
  const {
    data: lead,
    isLoading: leadLoading,
    error: leadError,
    refetch: refetchLead
  } = useLeadDetail(leadId, auth, { enabled: queryEnabled })

  const {
    data: meetings = [],
    isLoading: meetingsLoading
  } = useLeadMeetingActivities(leadId, auth, { enabled: queryEnabled })

  useEffect(() => {
    if (!isOpen) return
    setActiveTab(defaultTab)
  }, [isOpen, leadId, defaultTab])

  useEffect(() => {
    if (!lead) return
    setStatus(lead.status ?? 'new')
    setNotes(lead.notes ?? '')
  }, [lead])

  const handleStatusChange = useCallback(
    async (newStatus) => {
      if (!leadId || newStatus === status) return
      const previous = status
      setStatus(newStatus)
      setSavingStatus(true)
      try {
        const { error } = await updateLeadStatus(leadId, newStatus, auth)
        if (error) throw error
        onStatusChange?.()
      } catch (err) {
        setStatus(previous)
        toast.error(err?.message ?? 'Failed to update status')
      } finally {
        setSavingStatus(false)
      }
    },
    [leadId, status, auth, onStatusChange]
  )

  const handleSaveNotes = async () => {
    if (!leadId) return
    setSavingNotes(true)
    try {
      const { error } = await updateLeadNotes(leadId, notes, auth)
      if (error) throw error
      toast.success('Notes saved')
      await refetchLead()
    } catch (err) {
      toast.error(err?.message ?? 'Failed to save notes')
    } finally {
      setSavingNotes(false)
    }
  }

  if (!isOpen) return null

  const owner = lead ? resolveAgentOwner(lead) : null
  const surveyResponses = lead ? sortSurveyResponses(lead.marketing_form_responses) : []
  const createdAt = lead?.created_at ?? lead?.registered_at
  const titleName = lead?.full_name?.trim() || lead?.name?.trim() || 'Lead'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100 flex items-start justify-between gap-4 shrink-0">
          <div className="min-w-0 flex-1 flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-semibold text-slate-800 truncate">{titleName}</h3>
            {lead && !leadLoading && (
              <StatusBadgeDropdown
                status={status}
                onChange={handleStatusChange}
                disabled={savingStatus}
              />
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg shrink-0"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {leadLoading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-2 border-[#00AAFF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : leadError || !lead ? (
          <div className="p-8 text-center">
            <p className="text-sm text-red-600">
              {leadError?.message ?? 'Lead not found or you do not have access.'}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 text-sm font-medium text-[#00AAFF] hover:underline"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="px-6 border-b border-gray-200 flex gap-1 shrink-0">
              {['details', 'surveys'].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors capitalize ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {activeTab === 'surveys' ? (
                surveyResponses.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">No survey responses found.</p>
                ) : (
                  <ul className="space-y-3">
                    {surveyResponses.map((response) => {
                      const form = response.marketing_forms
                      const formTitle = Array.isArray(form) ? form[0]?.title : form?.title
                      return (
                        <li
                          key={response.id}
                          className="border border-gray-200 rounded-lg p-4 flex flex-wrap items-baseline justify-between gap-2"
                        >
                          <span className="font-medium text-gray-900">{formTitle || 'Form'}</span>
                          <div className="text-sm text-gray-500 flex flex-wrap gap-3">
                            <span>
                              {response.submitted_at
                                ? format(new Date(response.submitted_at), 'MMM d, yyyy h:mm a')
                                : '—'}
                            </span>
                            {response.score != null && (
                              <span className="text-gray-700">Score: {response.score}</span>
                            )}
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                )
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <DetailField label="Full Name">{lead.full_name || '—'}</DetailField>
                    <DetailField label="Email">{lead.email || '—'}</DetailField>
                    <DetailField label="Phone">{lead.phone || '—'}</DetailField>
                    <DetailField label="Invited By">{lead.invited_by || '—'}</DetailField>
                    <DetailField label="Invitation Link">{resolveSharingLinkLabel(lead)}</DetailField>
                    <DetailField label="Event">{lead.events?.title || '—'}</DetailField>
                    <DetailField label="Lead Owner">{owner?.full_name?.trim() || '—'}</DetailField>
                    <DetailField label="Created At">
                      {createdAt ? format(new Date(createdAt), 'MMM d, yyyy') : '—'}
                    </DetailField>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">1-1 Meeting History</h4>
                    {meetingsLoading ? (
                      <p className="text-sm text-gray-500">Loading meeting history…</p>
                    ) : meetings.length === 0 ? (
                      <p className="text-sm text-gray-500">No meetings logged yet.</p>
                    ) : (
                      <ul className="space-y-2">
                        {meetings.map((meeting) => {
                          const dateValue = meeting.activity_date || meeting.created_at
                          return (
                            <li
                              key={meeting.id}
                              className="flex gap-3 text-sm border border-gray-100 rounded-lg p-3"
                            >
                              <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900">
                                  {dateValue
                                    ? format(new Date(dateValue), 'MMM d, yyyy')
                                    : '—'}
                                </p>
                                {meeting.notes && (
                                  <p className="text-gray-600 mt-1 whitespace-pre-wrap">{meeting.notes}</p>
                                )}
                              </div>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      General Notes
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Add your notes about this lead…"
                    />
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={handleSaveNotes}
                        disabled={savingNotes}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {savingNotes ? 'Saving…' : 'Save Notes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>
  )
}

export default LeadDetailModal
